'use client';

import type { PaymentMethodDataEncCardForWorldpay } from '@payments/aurum-contracts';
import { createDebug } from '@shared/debug';
import { useScript } from '@shared/hooks';

type WorldPayDataToEncrypt = {
  cvc: string;
  cardHolderName: string;
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
};

type RawCardData = {
  cardNumber?: string;
  cvv?: string;
  expiryMonth?: string;
  expiryYear?: string;
  cardholderName?: string;
};

type TEncryptedWorldPayData = Pick<PaymentMethodDataEncCardForWorldpay, 'encData'>;

/**
 * handles the encryption of the Worldpay card data using the Worldpay encryption library
 * @returns the encrypted data
 */
const useWorldpayCardEncryption = () => {
  const debug = createDebug({ isEnabled: false });
  const { addScriptTag } = useScript();
  const WORLDPAY_ENCRYPTION_LIBRARY_SRC = '/lib/worldpay-cse-1.min.js';
  const WORLDPAY_PUBLIC_KEY =
    '1#10001#c8ad9b2d42cd926fa3829852be2a55959fedff7d353d42bd963604f818bc4780fdc8de98d95998022ed2e628b4292e2807425b93580f83de3cabf6a304bcb71c1c4ca18b67ed838102c065747597e90ed6181ce8f354a0e92a4b2fd0c2283b86dcfa6507c4115ec0133f29d74caadd336790b71870f320ac8d09347b21fb84c185effba64db3c64164f0b77a7e2632ca3c8d16e48b563554bbdc56652d78b062323ecf932844f524a7793a20dd9e145019faad374b2d74adcef39e114872826a53d83d19629153b1f8341d2c7c656130228b6d55017820853a31b8276e67ebb9ce812109f896c0bc6da729018da5d8defa4b0ee764d543219314ae0b42871c3f';

  // Track active encryption promises for cleanup
  const activeEncryptionPromises = new Set<Promise<string>>();

  /**
   * gets the encrypted data from the Worldpay encryption library
   * @param data - the data to encrypt
   * @returns the encrypted data
   */
  const removeFailedScriptTag = (scriptSrc: string) => {
    if (typeof document === 'undefined') return;
    const scriptTag = Array.from(document.querySelectorAll('script')).find(script => script.src?.includes(scriptSrc) || script.getAttribute('src')?.includes(scriptSrc));
    scriptTag?.remove();
  };

  const getEncryptedData = async (data: RawCardData): Promise<string> => {
    try {
      await addScriptTag(WORLDPAY_ENCRYPTION_LIBRARY_SRC);
    } catch (error) {
      removeFailedScriptTag(WORLDPAY_ENCRYPTION_LIBRARY_SRC);
      debug.forceError('Error loading Worldpay encryption library');
      throw new Error((error as Error).message || (error as string), { cause: error });
    }

    const encryptionPromise = new Promise<string>((resolve, reject) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).Worldpay.setPublicKey(WORLDPAY_PUBLIC_KEY);

        // Create a custom error handler that rejects the promise
        const handleError = (error: string) => {
          debug.forceError(`Worldpay encryption library failed to encrypt: ${error}`);
          reject(new Error(error));
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const encryptedData = (window as any).Worldpay.encrypt(dataToWorldPayData(data), handleError);

        // If the encryption is synchronous and successful, resolve immediately
        if (encryptedData) {
          resolve(encryptedData);
        }
      } catch (error) {
        debug.forceError('Error on getting encrypted data from the Worldpay encryption library');
        reject(new Error((error as Error).message || (error as string)));
      }
    });

    // Track the promise for cleanup
    activeEncryptionPromises.add(encryptionPromise);

    // Remove from tracking when promise resolves or rejects
    encryptionPromise.finally(() => {
      activeEncryptionPromises.delete(encryptionPromise);
    });

    return encryptionPromise;
  };

  /**
   * converts the data to the Worldpay data format
   * @param data - the data to convert
   * @returns the Worldpay data format
   */
  const dataToWorldPayData = (data: RawCardData): WorldPayDataToEncrypt => {
    return {
      cvc: data.cvv || '',
      cardHolderName: data.cardholderName || '',
      cardNumber: data.cardNumber || '',
      expiryMonth: data.expiryMonth || '',
      expiryYear: data.expiryYear || '',
    };
  };

  /**
   * cleans up any active encryption operations
   */
  const cleanup = () => {
    // Clear all active encryption promises
    activeEncryptionPromises.clear();
  };

  return {
    getEncryptedData,
    cleanup,
  };
};

export { useWorldpayCardEncryption };
export type { RawCardData, TEncryptedWorldPayData };
